/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import socket, { acompanharPedido } from '../lib/socket';

interface PixPaymentData {
  orderId: string;
  qrCode: string;
  pixCode: string;
  paymentLink: string;
  expiresAt: string;
  planName: string;
  clientName: string;
  amount: number;
}

/**
 * Processa o QR Code para garantir formato correto para exibição
 */
function processQrCode(qrCodeData: string | undefined | null): string {
  if (!qrCodeData) {
    return '';
  }

  // Se já é uma URL HTTP, retornar como está
  if (qrCodeData.startsWith('http://') || qrCodeData.startsWith('https://')) {
    return qrCodeData;
  }

  // Se é um Data URL (base64 com prefixo), retornar como está
  if (qrCodeData.startsWith('data:image')) {
    return qrCodeData;
  }

  // Se parece ser base64 puro (sem prefixo)
  if (/^[A-Za-z0-9+/]+=*$/.test(qrCodeData)) {
    return `data:image/png;base64,${qrCodeData}`;
  }

  // Se tem quebras de linha (base64 mal formatado)
  if (qrCodeData.includes('\n') || qrCodeData.includes('\r')) {
    const cleanBase64 = qrCodeData.replace(/[\r\n]/g, '');
    if (/^[A-Za-z0-9+/]+=*$/.test(cleanBase64)) {
      return `data:image/png;base64,${cleanBase64}`;
    }
  }

  // Se é código PIX EMV, gerar QR Code via API
  if (qrCodeData.startsWith('00020126') && qrCodeData.length > 100) {
    const encodedCode = encodeURIComponent(qrCodeData);
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedCode}`;
  }

  return '';
}

const PixPayment: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState<PixPaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setError('ID do pedido não fornecido');
      setLoading(false);
      return;
    }

    loadPaymentData();
  }, [orderId]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      // Revisão do #665: sem o nº do pedido nem o payload no console (o nº
      // abre esta página; o payload tem o código PIX).
      console.log('🔍 Carregando dados PIX');

      // Adicionar timestamp para evitar cache
      const timestamp = Date.now();
      const response = await api.get(`/payments/pix/${orderId}?t=${timestamp}`);

      // Compatibilidade com resposta encapsulada { success: true, data: {...} } ou direta
      const pixData = response.data?.data || response.data;
      console.log('✅ Dados PIX recebidos');
      setPaymentData(pixData);
    } catch (err) {
      console.error('❌ Erro ao carregar pagamento PIX:', err);
      setError('Erro ao carregar dados do pagamento PIX');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    if (!orderId) return;

    try {
      console.log('🔄 Verificando status do pagamento...');

      // Usa o mesmo endpoint que loadPaymentData para obter dados completos
      const timestamp = Date.now();
      const response = await api.get(`/payments/pix/${orderId}?t=${timestamp}`);

      // Compatibilidade com resposta encapsulada { success: true, data: {...} } ou direta
      const pixData = response.data?.data || response.data;
      console.log('📊 Status do pagamento:', pixData?.status ?? pixData?.paymentStatus);

      // Atualiza paymentData se recebeu qrCode/pixCode e ainda não temos
      if (pixData && (pixData.qrCode || pixData.pixCode)) {
        setPaymentData((prev) => {
          // Se já temos os dados completos, não precisa atualizar
          if (prev?.qrCode && prev?.pixCode) {
            return prev;
          }
          // Atualiza com os novos dados
          console.log('🔄 Atualizando paymentData com dados do PIX');
          return {
            ...prev,
            ...pixData,
          } as PixPaymentData;
        });
      }

      if (
        pixData.status === 'paid' ||
        pixData.status === 'approved' ||
        pixData.paymentStatus === 'paid' ||
        pixData.paymentStatus === 'approved'
      ) {
        console.log('✅ Pagamento aprovado! Redirecionando...');
        // replace: o Voltar não reabre esta página (o pedido fica fora do histórico)
        navigate('/compra-aprovada', { replace: true });
      }
    } catch (err) {
      console.error('❌ Erro ao verificar status:', err);
    }
  };

  // Listener para pagamento confirmado via WebSocket
  useEffect(() => {
    console.log('🎯 Configurando listener WebSocket');
    console.log('🔌 Socket conectado?', socket.connected);
    console.log('🆔 Socket ID:', socket.id);

    // Conectar se não estiver conectado
    if (!socket.connected) {
      console.log('🔄 Conectando socket...');
      socket.connect();
    }

    // O backend só manda o `payment-confirmed` para a room do pedido (#555).
    acompanharPedido(orderId);

    // Listener para conexão
    const onConnect = () => {
      console.log('✅ Socket conectado com ID:', socket.id);
    };
    socket.on('connect', onConnect);

    // Listener para desconexão
    const onDisconnect = () => {
      console.log('❌ Socket desconectado');
    };
    socket.on('disconnect', onDisconnect);

    // Listener para qualquer evento (debug)
    const onAnyEvent = (eventName: string) => {
      console.log('📡 Evento recebido:', eventName);
    };
    socket.onAny(onAnyEvent);

    const handlePaymentConfirmed = (data: any) => {
      console.log('🔔 Pagamento confirmado via WebSocket');

      // Comparar como string para garantir que funcione
      if (String(data.orderId) === String(orderId)) {
        console.log('✅ Pagamento confirmado para este pedido! Redirecionando...');
        navigate('/compra-aprovada', { replace: true });
      } else {
        console.log('⚠️ OrderId não corresponde ao pedido atual');
      }
    };

    socket.on('payment-confirmed', handlePaymentConfirmed);
    console.log('👂 Listener "payment-confirmed" registrado');

    return () => {
      console.log('🧹 Limpando listeners WebSocket');
      socket.off('payment-confirmed', handlePaymentConfirmed);
      // Só os próprios handlers: `off('connect')` sem handler apagaria o que
      // reentra na room do pedido a cada reconexão (lib/socket.ts).
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.offAny(onAnyEvent);
    };
  }, [orderId, navigate]);

  // Polling de status com backoff (#128): 3s → dobra até 30s, teto de 30min.
  // O WebSocket acima é o caminho rápido; isto é o fallback. Antes: 3s fixos
  // pra sempre (aba esquecida = 1.200 req/h por pedido).
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay = 3000;
    const deadline = Date.now() + 30 * 60 * 1000;

    const tick = async () => {
      if (cancelled) return;
      await checkPaymentStatus();
      if (cancelled || Date.now() > deadline) return;
      delay = Math.min(delay * 2, 30000);
      timer = setTimeout(tick, delay);
    };
    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId]);

  const copyPixCode = () => {
    if (paymentData?.pixCode) {
      navigator.clipboard.writeText(paymentData.pixCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando pagamento...</p>
        </div>
      </div>
    );
  }

  if (error || !paymentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Erro</h1>
          <p className="text-gray-600">{error || 'Pagamento não encontrado'}</p>
          <button
            onClick={() => {
              window.location.href = 'https://coracoespreciosos.com.br';
            }}
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Ir para o site
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <img src="/logo.svg" alt="Logo" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento PIX</h1>
          <p className="text-gray-600">Escaneie o QR Code ou copie o código PIX para pagar</p>
        </div>

        <div className="space-y-6">
          {/* Informações do Pedido */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Detalhes do Pedido</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p>
                <strong>Plano:</strong> {paymentData.planName}
              </p>
              <p>
                <strong>Cliente:</strong> {paymentData.clientName}
              </p>
              <p>
                <strong>Valor:</strong> R${' '}
                {typeof paymentData.amount === 'number' ? paymentData.amount.toFixed(2) : paymentData.amount || '0.00'}
              </p>
            </div>
          </div>

          {/* QR Code */}
          <div className="text-center">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 inline-block">
              {(() => {
                const qrCodeUrl = processQrCode(paymentData.qrCode);
                return qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt="QR Code PIX"
                    className="w-48 h-48 mx-auto"
                    onError={(e) => {
                      console.error('Erro ao carregar QR code:', e);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-48 h-48 mx-auto bg-gray-100 flex items-center justify-center">
                    <p className="text-gray-500 text-sm">QR Code não disponível</p>
                  </div>
                );
              })()}
            </div>
            <p className="text-sm text-gray-500 mt-2">Aponte a câmera do seu celular para o QR Code</p>
          </div>

          {/* Código PIX */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Código PIX</h3>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={paymentData.pixCode || 'Código PIX não disponível'}
                readOnly
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50"
              />
              <button
                onClick={copyPixCode}
                disabled={!paymentData.pixCode}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  copied
                    ? 'bg-green-500 text-white'
                    : paymentData.pixCode
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {paymentData.pixCode ? 'Cole este código no seu app bancário' : 'Código PIX não disponível'}
            </p>
          </div>

          {/* Instruções */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Como pagar:</h3>
            <ol className="text-sm text-blue-800 space-y-1">
              <li>1. Abra o app do seu banco</li>
              <li>2. Escolha a opção PIX</li>
              <li>3. Escaneie o QR Code ou cole o código</li>
              <li>4. Confirme o pagamento</li>
              <li>5. Aguarde a confirmação automática</li>
            </ol>
          </div>

          {/* Status */}
          <div className="text-center">
            <div className="animate-pulse">
              <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Aguardando pagamento...</p>
            </div>
          </div>

          {/* Botão Voltar */}
          <div className="text-center">
            <button
              onClick={() => {
                window.location.href = 'https://coracoespreciosos.com.br';
              }}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ← Voltar ao site
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PixPayment;
